import M from 'materialize-css';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LuPackageOpen } from 'react-icons/lu';
import { BsFiletypeXml } from 'react-icons/bs';
import { IoMdSwap } from 'react-icons/io';
import { MdLocationPin } from 'react-icons/md';
import { useQuery } from '@tanstack/react-query';
import api from '../../common/api/index.ts';
import endpoints from '../../common/endpoints.ts';
import { PiBuildings, PiCreditCardLight, PiUsersLight } from 'react-icons/pi';
import { AiOutlineCloudUpload } from 'react-icons/ai';
import { TfiControlBackward } from 'react-icons/tfi';
import { TbArrowsTransferDown } from 'react-icons/tb';
import NotificationCounter from '../NotificationCounter.tsx';
import queryHooks from '../../common/queryHooks/index.ts';
import { OutboundDocumentStatusEnum } from '../../common/enums.ts';

const Sidenav = props => {
  const { logout, userType } = props;

  const { pathname } = useLocation();
  const navRef = useRef();

  const [fastSpringProducts, setFastSpringProducts] = useState([]);

  const { data: user } = useQuery({
    queryKey: [endpoints.user.read.one.by.id()],
    queryFn: api.user.read.one.by.id,
    refetchOnMount: false,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    enabled: userType === 'customer',
  });

  const { data: fastSpringOrders } = useQuery({
    queryKey: [endpoints.fastspring.read.all.orders.by.userId()],
    queryFn: api.fastspring.read.all.orders.by.userId,
    refetchOnMount: false,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    enabled: userType === 'customer',
  });

  useEffect(() => {
    if (fastSpringOrders?.length) {
      const products = fastSpringOrders.flatMap(obj => {
        return obj.items.map(item => {
          return item.product;
        });
      });

      setFastSpringProducts(products);
    }
  }, [fastSpringOrders]);

  useEffect(() => {
    const nav = M.Sidenav.init(navRef.current);

    if (nav) {
      nav.open();
      return () => {
        nav.destroy();
      };
    }
  }, []);

  const userListItem = useMemo(
    () => (
      <li>
        <div className="user-view" style={{ backgroundColor: '#eeeeeeaa' }}>
          <p className="name" style={{ margin: '0px' }}>
            {user?.firstName} {user?.lastName}
            <span className="grey-text text-darken-1">
              {user?.title ? ` - ${user.title}` : ''}
            </span>
          </p>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              minHeight: '50px',
            }}
          >
            <a
              className="email"
              href="/"
              onClick={logout}
              style={{ padding: '0px', margin: '0px', display: 'inline' }}
            >
              Sign Out
            </a>
          </div>
        </div>
      </li>
    ),
    [user, logout]
  );

  if (!userType || userType === 'trading partner')
    return (
      <div style={{ margin: '0px', height: '0px', padding: '0px' }}>
        <ul ref={navRef} id="side-navigation" className="sidenav sidenav-fixed">
          {userListItem}
          <li style={{ marginTop: '20px' }}>
            <Link to="/" className={pathname === '/' ? 'red-text' : ''}>
              <i
                className={`material-icons ${pathname === '/' ? 'red-text' : ''}`}
              >
                home
              </i>
              Home
            </Link>
          </li>
        </ul>
      </div>
    );

  return (
    <div style={{ margin: '0px', height: '0px', padding: '0px' }}>
      <ul ref={navRef} id="side-navigation" className="sidenav sidenav-fixed">
        {userListItem}
        <li style={{ marginTop: '20px' }}>
          <Link to="/" className={pathname === '/' ? 'red-text' : ''}>
            <i
              className={`material-icons ${pathname === '/' ? 'red-text' : ''}`}
            >
              home
            </i>
            Home
          </Link>
        </li>
        <li>
          <Link
            to="/gln"
            className={pathname.includes('/gln') ? 'red-text' : ''}
          >
            <i
              className={`material-icons ${
                pathname.includes('/gln') ? 'red-text' : ''
              }`}
            >
              <MdLocationPin />
            </i>
            My GLNs
          </Link>
        </li>
        <li>
          <Link
            to="/tradingpartner"
            className={pathname.includes('/tradingpartner') ? 'red-text' : ''}
          >
            <i
              className={`material-icons ${
                pathname.includes('/tradingpartner') ? 'red-text' : ''
              }`}
            >
              <IoMdSwap />
            </i>
            Trading Partners
          </Link>
        </li>
        <li>
          <Link
            to="/orders"
            className={pathname === '/orders' ? 'red-text' : ''}
          >
            <i
              className={`material-icons ${
                pathname === '/orders' ? 'red-text' : ''
              }`}
            >
              <LuPackageOpen />
            </i>
            My Orders
          </Link>
        </li>
        <li>
          <Link
            to="/searchmeds"
            className={pathname === '/searchmeds' ? 'red-text' : ''}
          >
            <i
              className={`material-icons ${
                pathname === '/searchmeds' ? 'red-text' : ''
              }`}
            >
              content_paste
            </i>
            My Pedigrees
          </Link>
        </li>
        <li>
          <Link
            to="/sendapedigree"
            className={pathname === '/sendapedigree' ? 'red-text' : ''}
          >
            <i
              className={`material-icons ${
                pathname === '/sendapedigree' ? 'red-text' : ''
              }`}
            >
              content_paste_go
            </i>
            Send A Pedigree
          </Link>
        </li>
        <li>
          <Link
            to="/orderupload"
            className={pathname === '/orderupload' ? 'red-text' : ''}
          >
            <i
              className={`material-icons ${
                pathname === '/orderupload' ? 'red-text' : ''
              }`}
            >
              upload_file
            </i>
            Upload An Order{' '}
          </Link>
        </li>
        <li style={{ position: 'relative' }}>
          <Link
            to="/sentepcisdocs"
            className={pathname.includes('/sentepcisdocs') ? 'red-text' : ''}
          >
            <i
              className={`material-icons ${pathname.includes('/sentepcisdocs') ? 'red-text' : ''}`}
            >
              outgoing_mail
            </i>
            <NotificationCounter
              query={
                queryHooks.outboundDocumentStatus.useReadStatusCountByUserId
              }
              options={{
                select: data => {
                  if (!Array.isArray(data) || !data.length) return 0;
                  return (
                    data.filter(
                      ({ status }) =>
                        status === OutboundDocumentStatusEnum.failed
                    )?.[0]?.count || 0
                  );
                },
              }}
            />
            Sent EPCIS Docs
          </Link>
        </li>
        <li>
          <Link
            to="/reportanincident"
            className={pathname === '/reportanincident' ? 'red-text' : ''}
          >
            <i
              className={`material-icons ${
                pathname === '/reportanincident' ? 'red-text' : ''
              }`}
            >
              report_problem
            </i>
            Report An Incident
          </Link>
        </li>
        <li style={{ position: 'relative' }}>
          <Link
            to="/incidenttracker"
            className={pathname === '/incidenttracker' ? 'red-text' : ''}
          >
            <i
              className={`material-icons ${
                pathname === '/incidenttracker' ? 'red-text' : ''
              }`}
            >
              sync_problem
            </i>
            <NotificationCounter
              query={queryHooks.illegitReport.useReadCountByUserId}
            />
            Incident Tracker
          </Link>
        </li>
        <li style={{ position: 'relative' }}>
          <Link
            to="/recalls"
            className={pathname === '/recalls' ? 'red-text' : ''}
          >
            <i
              className={`material-icons ${
                pathname === '/recalls' ? 'red-text' : ''
              }`}
            >
              <TfiControlBackward />
            </i>
            <NotificationCounter
              query={queryHooks.recall.useReadOneActiveRecallCountByUserId}
              select={data => data?.[0].affectedMedCount}
            />
            Recalls
          </Link>
        </li>
        <li style={{ position: 'relative' }}>
          <Link
            to="/exceptions"
            className={pathname === '/exceptions' ? 'red-text' : ''}
          >
            <i
              className={`material-icons ${
                pathname === '/recalls' ? 'red-text' : ''
              }`}
            >
              <TbArrowsTransferDown />
            </i>
            <NotificationCounter
              query={
                queryHooks.documentException.useGetDocumentExceptionErrorsCount
              }
            />
            Exceptions
          </Link>
        </li>
        {(fastSpringProducts.includes('dscsa-sops') ||
          fastSpringProducts.includes('dscsa-sop-creation')) && (
          <li>
            <Link to="/sop" className={pathname === '/sop' ? 'red-text' : ''}>
              <i className="material-icons">
                <AiOutlineCloudUpload
                  className={pathname === '/sop' ? 'red-text' : ''}
                />
              </i>
              Upload SOP
            </Link>
          </li>
        )}
        {(process.env.REACT_APP_FEATURE_ENV === 'dev' ||
          process.env.REACT_APP_FEATURE_ENV === 'test') && (
          <li>
            <Link
              to="/demo/epcis/file/generator"
              className={
                pathname === '/demo/epcis/file/generator' ? 'red-text' : ''
              }
            >
              <i className="material-icons">
                <BsFiletypeXml
                  className={
                    pathname === '/demo/epcis/file/generator' ? 'red-text' : ''
                  }
                />
              </i>
              Demo EPCIS File Generator
            </Link>
          </li>
        )}
        <div style={{ marginTop: '40px' }} className="divider" />
        {user && user?.id === user?.customer.adminId && (
          <>
            <li>
              <Link
                to="/subscription"
                className={pathname.includes('/subscription') ? 'red-text' : ''}
              >
                <i className="material-icons">
                  <PiCreditCardLight
                    className={
                      pathname.includes('/subscription') ? 'red-text' : ''
                    }
                  />
                </i>
                Manage Subscription
              </Link>
            </li>
            <li>
              <Link
                to="/locations"
                className={pathname.startsWith('/locations') ? 'red-text' : ''}
              >
                <i className="material-icons">
                  <PiBuildings
                    className={
                      pathname.startsWith('/locations') ? 'red-text' : ''
                    }
                  />
                </i>
                Manage Locations
              </Link>
            </li>
          </>
        )}
        {user &&
        (user?.id === user?.customer.adminId ||
          user.userCustomerLocations?.some(ucl => ucl.isAdmin)) ? (
          <li>
            <Link
              to="/users"
              className={pathname.includes('/users') ? 'red-text' : ''}
            >
              <i className="material-icons">
                <PiUsersLight
                  className={pathname.startsWith('/users') ? 'red-text' : ''}
                />
              </i>
              Manage Users
            </Link>
          </li>
        ) : null}
        <li>
          <Link
            to="/settings/changepassword"
            className={pathname.includes('/settings') ? 'red-text' : ''}
          >
            <i
              className={`material-icons ${
                pathname.includes('/settings') ? 'red-text' : ''
              }`}
            >
              settings
            </i>
            Settings
          </Link>
        </li>{' '}
      </ul>
      <a
        href="/"
        data-target="side-navigation"
        className="sidenav-trigger white-text hide-on-large-only"
      >
        <i className="material-icons">menu</i>
      </a>
    </div>
  );
};

export default Sidenav;
